import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import noInvoicesImg from "assets/img/no-invoices.svg"
import "./AssetInvoicesOutgoing.scss"

import { Guid, IMultipleEntities } from "@/types"
import Button from "components/Button/Button"
import toastMessages from "stores/toastMessages"
import FinanceService from "services/FinanceService"
import usePagedData from "hooks/usePagedData"
import Text from "components/Text/Text"
import { Flex } from "components/Flex"
import AssetInvoiceOutgoingByRequest, { AssetInvoiceOutgoingType } from "models/AssetInvoiceOutgoing"
import InfiniteScroll from "react-infinite-scroller"
import AssetInvoicesByRequest from "components/AssetInvoicesByRequest/AssetInvoicesByRequest"
import Spinner from "components/Spinner/Spinner"
import AmountWithCurrency from "models/AmountWithCurrency"

interface IAssetInvoicesOutgoingProps {
    assetId: Guid
    scrollEl?: React.RefObject<HTMLDivElement | null>
}

const AssetInvoicesOutgoing: FunctionComponent<React.PropsWithChildren<IAssetInvoicesOutgoingProps>> = ({
    assetId,
    scrollEl,
}) => {
    const { t } = useTranslation()
    const invoicesParams = useMemo(() => {
        return {
            assetId,
            pageSize: 30,
            type: AssetInvoiceOutgoingType.nonSale
        }
    }, [assetId])

    const [expandingState, setExpandingState] = useState<Record<Guid, boolean | undefined>>({})
    const [saleAssetOutgoingInvoicesByRequest, setSaleAssetOutgoingInvoicesByRequest] = useState<IMultipleEntities<AssetInvoiceOutgoingByRequest, { totalAmount?: AmountWithCurrency, rentalFeeAmount?: AmountWithCurrency }>>()

    const fetchSaleInvoicesByRequest = useCallback(async () => {
        try {
            const data = await FinanceService.getAssetOutgoingInvoicesByRequest({
                assetId,
                pageSize: 30,
                type: AssetInvoiceOutgoingType.sale
            })
            setSaleAssetOutgoingInvoicesByRequest(data)
        } catch (e) {
            toastMessages.showSystemError()
        }
    }, [assetId])

    const fetchNonSaleInvoicesByRequest = useCallback(() => {
        return FinanceService.getAssetOutgoingInvoicesByRequest(invoicesParams)
    }, [invoicesParams])

    const {
        items: nonSaleInvoicesByRequest,
        loadNextPage,
        isInitialLoad,
        isLoading,
        hasMore,
        aggregate: nonSaleAggregate
    } = usePagedData<AssetInvoiceOutgoingByRequest, { totalAmount?: AmountWithCurrency, rentalFeeAmount?: AmountWithCurrency }>(fetchNonSaleInvoicesByRequest, toastMessages.showSystemError)

    const toggleExpanding = useCallback((value: boolean, id: Guid,) => {
        setExpandingState(prev => ({ ...prev, [id]: value }))
    }, [])

    const handleHideAll = useCallback(() => {
        setExpandingState({})
    }, [])

    const handleExpandAll = useCallback(() => {
        if (!nonSaleInvoicesByRequest) return
        const expandedAllState = nonSaleInvoicesByRequest.reduce((accum, item) => ({ ...accum, [item.requestId]: true }), {})
        setExpandingState(expandedAllState)
    }, [nonSaleInvoicesByRequest])
    const isAllItemsExpanded = useMemo(() => {
        const expandingArray = Object.values(expandingState);
        return expandingArray.length === nonSaleInvoicesByRequest?.length && expandingArray.every(val => val)
    }, [expandingState, nonSaleInvoicesByRequest?.length])


    useEffect(() => {
        fetchSaleInvoicesByRequest()

    }, [fetchSaleInvoicesByRequest])


    const renderHeader = useCallback((title: string, data: { totalAmount?: AmountWithCurrency, rentalFeeAmount?: AmountWithCurrency }, showExpanding?: boolean) =>
    (<Flex justifyContent="space-between" className="mb-2">
        <Flex flow="column">
            <Text type="h3">{title}</Text>
            {showExpanding && (isAllItemsExpanded ?
                <Button className="expand-hide-all-button" theme="link" label={t("common.hideAll")} onClick={handleHideAll} /> :
                <Button className="expand-hide-all-button" theme="link" label={t("common.expandAll")} onClick={handleExpandAll} />)
            }
        </Flex>
        <Flex flow="column">
            {data.totalAmount && <div className="font-size-h3 text-right">{data.totalAmount.print()}</div>}
            {data.rentalFeeAmount && <Text className="color-grey-500 text-right">{t("invoices.rentalFees")}: {data.rentalFeeAmount.print()}</Text>}
        </Flex>
    </Flex>
    ), [handleExpandAll, handleHideAll, isAllItemsExpanded, t])

    return (
        <>
            <Text type="h3" className="mb-3">
                {t('asset.outgoingInvoicesTitle')}
            </Text>
            <InfiniteScroll
                pageStart={0}
                loadMore={loadNextPage}
                initialLoad={isInitialLoad}
                hasMore={hasMore}
                getScrollParent={() => scrollEl?.current || null}
                useWindow={false}>
                <>
                    {!!saleAssetOutgoingInvoicesByRequest?.list.length && <>
                        {renderHeader(t("purposes.sale"), saleAssetOutgoingInvoicesByRequest.aggregates!)}
                        <AssetInvoicesByRequest isSale assetId={assetId} data={saleAssetOutgoingInvoicesByRequest.list[0]} />
                    </>
                    }
                    {!!nonSaleInvoicesByRequest?.length && <>
                        {renderHeader(t("purposes.nonSale"), nonSaleAggregate!, true)}
                        {nonSaleInvoicesByRequest?.map(data => <AssetInvoicesByRequest assetId={assetId} data={data} toggleExpanded={toggleExpanding} expanded={expandingState[data.requestId]} />)}</>}
                    {isLoading &&
                        <div className="py-4">
                            <Spinner />
                        </div>
                    }
                    {!isLoading && (!saleAssetOutgoingInvoicesByRequest?.list.length && !nonSaleInvoicesByRequest?.length) &&
                        <div className="text-center py-4">
                            <img src={noInvoicesImg} alt="" className="mb-4" />
                            <Text type="p" className="white-space-pre-line">{t('invoices.noInvoicesAvailable')}</Text>
                        </div>}
                </>
            </InfiniteScroll>
        </>
    )
}

export default AssetInvoicesOutgoing
